import { useCallback } from "react";
import { useLevelTestContext } from "@/contexts/LevelTestContext";
import { useLevelTestProgress } from "@/contexts/LevelTestProgressContext";
import { LevelTestAnswer } from "@/services/student/level-test.interfaces";
import { delay } from "@/lib/utils";
import * as levelTestService from "@/services/student/level-test.service";

// Dados mock removidos - agora usamos service selector que decide entre mock/API

/**
 * IMPORTANTE: Hooks neste arquivo que usam `useLevelTestProgress` (como `useSubmitAnswer`)
 * REQUEREM que o componente que os utiliza esteja renderizado dentro de um
 * `LevelTestProgressProvider`. Caso contrário, um erro será lançado.
 * 
 * As rotas de level test (`/student/level-test/*`) já estão envolvidas com o provider
 * em `StudentRoutes.tsx`. Se você criar novas telas que usam esses hooks, certifique-se
 * de envolvê-las com `LevelTestProgressProvider`.
 */

/**
 * Hook para gerenciar status do teste de nivelamento
 */
export const useLevelTestStatus = () => {
  const { state } = useLevelTestContext();

  return {
    isTestCompleted: state.isTestCompleted,
    currentQuestionIndex: state.currentQuestionIndex,
    questionsHistory: state.questionsHistory,
    currentQuestion: state.currentQuestion,
    sessionId: state.sessionId,
    hasAnswers: state.answers.length > 0,
    answers: state.answers,
  };
};

/**
 * Hook para executar o teste de nivelamento (snapshot + getNext)
 */
export const useLevelTestRunner = () => {
  const { state, actions } = useLevelTestContext();

  const runLevelTest = useCallback(async () => {
    console.log("🔍 [HOOK] runLevelTest - Starting, current state:", {
      sessionId: state.sessionId,
      currentQuestion: state.currentQuestion?.id,
      questionsHistoryCount: state.questionsHistory.length
    });
    
    try {
      // 1. Carregar snapshot e popular histórico (readonly)
      try {
        console.log("🔍 [HOOK] runLevelTest - Getting session snapshot...");
        const snapshot = await levelTestService.getSessionSnapshot();
        
        console.log("🔍 [HOOK] runLevelTest - Snapshot received:", {
          sessionId: snapshot.sessionId,
          answeredCount: snapshot.answered.length,
          isTestCompleted: snapshot.isTestCompleted
        });

        // Salvar sessionId do snapshot
        if (snapshot.sessionId) {
          console.log("🔍 [HOOK] runLevelTest - Setting sessionId:", snapshot.sessionId);
          actions.setSessionId(snapshot.sessionId);
        }

        // Adicionar questões respondidas ao histórico (readonly)
        snapshot.answered.forEach((item, index) => {
          console.log(`🔍 [HOOK] runLevelTest - Processing answered question ${index + 1}:`, {
            order: item.order,
            questionId: item.question.id,
            questionText: item.question.questionText,
            optionsCount: item.question.options?.length || 0,
            options: item.question.options?.map(opt => ({
              id: opt.id,
              text: opt.text,
              order: opt.order
            })) || []
          });
          
          actions.addToHistory(item.question);

          // Mapear do formato do backend para o formato do frontend
          const backendAnswer = item.answer as any;

          // AnswerData agora contém Orders (números) ao invés de IDs
          const selectedOrders: number[] = backendAnswer.answerData
            ? JSON.parse(backendAnswer.answerData)
            : [];

          console.log(`🔍 [HOOK] runLevelTest - Converting answer for question ${item.question.id}:`, {
            backendAnswer,
            selectedOrders
          });

          // Converter Orders para IDs atuais da questão
          const selectedOptionIds = selectedOrders
            .map(
              (order) =>
                item.question.options.find((opt: any) => opt.order === order)
                  ?.id
            )
            .filter((id) => id !== undefined);

          const ans: LevelTestAnswer = {
            questionId: backendAnswer.exerciseId || item.question.id,
            selectedOptionIds: selectedOptionIds,
            isCorrect: backendAnswer.isCorrect,
            answeredAt: backendAnswer.answeredAt,
            timeSpentMs: 0, // Backend não retorna este campo no snapshot
          };
          
          console.log(`🔍 [HOOK] runLevelTest - Submitting converted answer:`, ans);
          actions.submitAnswer(ans);
        });
      } catch (snapshotError) {
        console.log("🔍 [HOOK] runLevelTest - Snapshot error (continuing normally):", snapshotError);
        // snapshot pode não existir; continuar normalmente
      }

      // 2. Buscar próxima questão (questão atual editável)
      console.log("🔍 [HOOK] runLevelTest - Getting next question...");
      const next = await levelTestService.getNextQuestion();
      
      console.log("🔍 [HOOK] runLevelTest - Next question response:", {
        sessionId: next.sessionId,
        questionNumber: next.questionNumber,
        isTestCompleted: next.isTestCompleted,
        hasQuestion: !!next.question,
        questionDetails: next.question ? {
          id: next.question.id,
          questionText: next.question.questionText,
          questionType: next.question.questionType,
          optionsCount: next.question.options?.length || 0,
          options: next.question.options?.map(opt => ({
            id: opt.id,
            text: opt.text,
            order: opt.order
          })) || []
        } : null
      });

      // Salvar sessionId se não foi salvo ainda
      if (next.sessionId && !state.sessionId) {
        console.log("🔍 [HOOK] runLevelTest - Setting sessionId from next question:", next.sessionId);
        actions.setSessionId(next.sessionId);
      }

      if (next.isTestCompleted) {
        console.log("🔍 [HOOK] runLevelTest - Test completed, getting results...");
        // Teste já concluído - obter resultado
        const resultResponse = await levelTestService.getLevelTestResult({
          sessionId: next.sessionId!,
        });
        actions.completeTest(resultResponse);
      } else if (next.question) {
        console.log("🔍 [HOOK] runLevelTest - Setting current question:", next.question.id);
        // Definir questão atual (editável)
        actions.setCurrentQuestion(next.question);
      } else {
        console.warn("🔍 [HOOK] runLevelTest - No question received and test not completed!");
      }

      console.log("🔍 [HOOK] runLevelTest - Completed successfully");
      return { success: true };
    } catch (error) {
      console.error("🔍 [HOOK] runLevelTest - Error:", error);
      return { success: false, error: "Failed to run test" };
    }
  }, [actions, state.sessionId]);

  return { runLevelTest };
};

/**
 * Hook para submeter resposta e obter próxima questão
 * 
 * REQUER: Componente deve estar dentro de `LevelTestProgressProvider`
 * (já aplicado nas rotas `/student/level-test/*` em `StudentRoutes.tsx`)
 */
export const useSubmitAnswer = () => {
  const { state, actions } = useLevelTestContext();
  const { refresh: refreshProgress } = useLevelTestProgress();

  const submitAnswer = useCallback(
    async (
      questionId: string,
      selectedOptionIds: string[] // Nota: MultipleChoices agora envia IDs
    ) => {
      try {
        // Obter questão atual (a que está sendo respondida)
        const currentQuestion = state.currentQuestion;
        if (!currentQuestion || currentQuestion.id !== questionId) {
          throw new Error(
            "Question not found or does not match current question"
          );
        }

        // Converter IDs para Orders antes de enviar ao backend
        const selectedOrders = selectedOptionIds
          .map(
            (id) =>
              currentQuestion.options.find((opt: any) => opt.id === id)?.order
          )
          .filter((order) => order !== undefined);

        // Converter Orders (números) para strings conforme esperado pelo backend
        const selectedOrdersAsStrings = selectedOrders.map((order) =>
          order.toString()
        );

        // Usar service selector para submeter resposta
        const response = await levelTestService.submitAnswer({
          questionId,
          selectedOptionIds: selectedOrdersAsStrings, // Orders como strings
          sessionId: state.sessionId!,
          timeSpentMs: 30000, // TODO: Calcular tempo real gasto
        });

        // Criar resposta para o contexto
        const answer: LevelTestAnswer = {
          questionId,
          selectedOptionIds: selectedOptionIds, // Mantém interface original com textos
          isCorrect: response.answer.isCorrect,
          answeredAt: response.answer.answeredAt,
          timeSpentMs: 30000, // Mock: 30 segundos
        };

        actions.submitAnswer(answer);

        // Atualizar dados de debug do progresso após submit
        if (state.sessionId) {
          try {
            await refreshProgress(state.sessionId);
          } catch (error) {
            // Não bloquear o fluxo se o refresh falhar
            console.warn('[useSubmitAnswer] Failed to refresh progress debug:', error);
          }
        }

        // Para compatibilidade com MultipleChoices, determinar opções corretas da questão atual
        const correctOptionTexts = (JSON.parse(response.answer.answerData) as unknown[]).map(
          (x: unknown) => x?.toString()
        );

        const result = {
          isCorrect: response.answer.isCorrect,
          feedback: response.answer.isCorrect ? "Correto!" : "Incorreto",
          expectedAnswer: correctOptionTexts,
          hasNextQuestion: !response.isTestComplete,
          sessionComplete: response.isTestComplete,
        };

        // Mover questão atual para histórico e buscar próxima
        try {
          // Adicionar questão respondida ao histórico
          if (state.currentQuestion) {
            actions.addToHistory(state.currentQuestion);
          }

          const next = await levelTestService.getNextQuestion();

          if (next.isTestCompleted) {
            // Teste concluído - obter resultado
            const resultResponse = await levelTestService.getLevelTestResult({
              sessionId: state.sessionId!,
            });
            actions.completeTest(resultResponse);
          } else if (next.question) {
            // Definir próxima questão como atual após delay
            delay(1000).then(() => {
              actions.setCurrentQuestion(next.question!);
            });
          }
        } catch (error) {
          console.error("Error getting next question:", error);
        }

        return result;
      } catch (error) {
        console.error("Error submitting answer:", error);
        throw error;
      }
    },
    [state, actions, refreshProgress]
  );

  return { submitAnswer };
};

/**
 * Hook para carregar próxima questão (usado internamente)
 */
export const useNextQuestion = () => {
  const loadNextQuestion = useCallback(async () => {
    try {
      // Nota: No novo fluxo, próximas questões vêm via submitAnswer response
      // Este hook agora tenta buscar explicitamente a próxima questão se necessário
      const next = await levelTestService.getNextQuestion();
      return next;
    } catch (error) {
      console.error("Error loading next question:", error);
      throw error;
    }
  }, []);

  return { loadNextQuestion };
};

/**
 * Hook para obter resultado do teste
 */
export const useLevelTestResult = () => {
  const { state } = useLevelTestContext();

  return {
    result: state.result,
    isTestCompleted: state.isTestCompleted,
    hasResult: !!state.result,
  };
};

/**
 * Hook principal que combina todas as funcionalidades
 */
export const useLevelTest = () => {
  const status = useLevelTestStatus();
  const runner = useLevelTestRunner();
  const submitAnswer = useSubmitAnswer();
  const nextQuestion = useNextQuestion();
  const result = useLevelTestResult();
  const { actions } = useLevelTestContext();

  return {
    // Status
    ...status,

    // Actions
    runLevelTest: runner.runLevelTest,
    submitAnswer: submitAnswer.submitAnswer,
    loadNextQuestion: nextQuestion.loadNextQuestion,
    resetTest: actions.resetTest,

    // Result
    ...result,
  };
};

// Export individual hooks for granular usage
export default useLevelTest;
